import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Typography, Box, CircularProgress } from '@mui/material';
import { useValue } from '@tldraw/state-react';
import { T } from '@tldraw/validate';
import {
  BaseBoxShapeUtil,
  HTMLContainer,
  useEditor,
  toDomPrecision,
} from 'tldraw';
import SimpleContent from 'components/simpleContent';
import noFavicon from 'components/simpleContent/no_favicon.svg';
import { useBoardActivities } from '../context/BoardActivitiesContext';
import { useBoardStore } from '../store/useBoardStore';
import { getPinDomain, getPinCardSize, getBoardYouTubeVideo, getActivityId } from '../utils/autoLayout';
import { isGenericYouTubeLabel } from '../utils/youtubeUrl';
import { usePinCardInViewport } from '../hooks/usePinCardInViewport';
import { isCanvasStrokeInProgress } from '../utils/canvasToolState';
import { startBoardPinPointerInteraction } from '../utils/pinCardDrag';
import { flushBoardPlacements } from '../utils/boardPlacementPersist';
import { openPinContextMenu } from '../utils/openTldrawContextMenu';
import { ledgerHeaderTextForShape } from '../utils/withdrawConstraints';
import { LEDGER_ACCENT_ON_LIGHT } from '../utils/ledgerAccent';

const pinCardShapeProps = {
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,
};

function useAutoPinCardHeight(editor, shape, activity) {
  const contentRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || !activity || editor.getInstanceState().isReadonly) return undefined;
    if (shape.meta?.manualHeight) return undefined;

    const syncHeight = () => {
      if (isCanvasStrokeInProgress(editor)) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const currentShape = editor.getShape(shape.id);
        if (!currentShape || currentShape.meta?.manualHeight) return;

        // Measure the card only — hover comments overflow outside the shape so they
        // do not inflate the hit target and block pins underneath.
        const cardEl = el.querySelector('.board-pin-card');
        const measured = Math.ceil((cardEl || el).scrollHeight);
        if (measured < 80) return;

        const nextHeight = Math.max(measured, 80);
        if (Math.abs(nextHeight - currentShape.props.h) <= 2) return;

        editor.updateShape({
          id: shape.id,
          type: 'pin-card',
          props: { h: nextHeight },
        });
      });
    };

    const resizeObserver = new ResizeObserver(syncHeight);
    resizeObserver.observe(el);

    const image = el.querySelector('img');
    if (image) {
      if (image.complete) syncHeight();
      else image.addEventListener('load', syncHeight);
    } else {
      syncHeight();
    }

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (image) image.removeEventListener('load', syncHeight);
    };
  }, [editor, shape.id, shape.props.w, shape.meta?.manualHeight, activity]);

  return contentRef;
}

function useAutoPinCardWidth(editor, shape, activity) {
  useEffect(() => {
    if (!activity || editor.getInstanceState().isReadonly) return undefined;
    if (shape.meta?.manualHeight) return undefined;
    if (isCanvasStrokeInProgress(editor)) return undefined;

    const { width } = getPinCardSize(activity);
    const currentShape = editor.getShape(shape.id);
    if (!currentShape || Math.abs(width - currentShape.props.w) <= 2) return undefined;

    editor.updateShape({
      id: shape.id,
      type: 'pin-card',
      props: { w: width },
    });
  }, [editor, shape.id, shape.props.w, shape.meta?.manualHeight, activity]);
}

function useAllowPinPointerEvents(editor) {
  return useValue(
    'pin-card-pointer-events',
    () => editor.isIn('select'),
    [editor]
  );
}

function PinCardShapeComponent({ shape }) {
  const editor = useEditor();
  const { activityById, loadedTime } = useBoardActivities();
  const activity = activityById(shape.meta?.activityId);
  const contentRef = useAutoPinCardHeight(editor, shape, activity);
  useAutoPinCardWidth(editor, shape, activity);
  const allowPinInteraction = useAllowPinPointerEvents(editor);
  const constraints = useBoardStore(s => s.board?.contextRecommendations?.constraints);
  const ledgerHeaderText = useMemo(
    () => ledgerHeaderTextForShape(constraints, shape),
    [constraints, shape]
  );
  const setSelectedActivityId = useBoardStore(s => s.setSelectedActivityId);
  const setFocusedPinId = useBoardStore(s => s.setFocusedPinId);
  const openYoutubePlayback = useBoardStore(s => s.openYoutubePlayback);
  const updateActivityLocal = useBoardStore(s => s.updateActivityLocal);
  const youtubeVideo = getBoardYouTubeVideo(activity);

  const activityId = getActivityId(activity);
  const activityTitle = activity?.content?.[0]?.title || '';
  useEffect(() => {
    if (!youtubeVideo?.videoId || !isGenericYouTubeLabel(activityTitle)) return undefined;
    if (!activityId) return undefined;
    let cancelled = false;
    const watchUrl = `https://www.youtube.com/watch?v=${encodeURIComponent(youtubeVideo.videoId)}`;
    fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        const nextTitle = String(data?.title || '').trim();
        if (cancelled || !nextTitle || isGenericYouTubeLabel(nextTitle)) return;
        const current = useBoardStore.getState().activities.find(item => getActivityId(item) === activityId);
        const content0 = current?.content?.[0];
        if (!content0 || !isGenericYouTubeLabel(content0.title)) return;
        updateActivityLocal(activityId, {
          content: [{ ...content0, title: nextTitle }],
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [activityId, activityTitle, updateActivityLocal, youtubeVideo?.videoId]);

  // Screenshot tier only needs idle zoom — avoid re-renders during zoom animation.
  const lastIdleZoomRef = useRef(editor.getCamera().z || 1);
  const viewZoom = useValue(
    'pin-card-view-zoom-idle',
    () => {
      const z = editor.getCamera().z || 1;
      if (editor.getCameraState() === 'moving') return lastIdleZoomRef.current;
      lastIdleZoomRef.current = z;
      return z;
    },
    [editor]
  );
  const loadImageWhen = usePinCardInViewport(editor, shape);

  const handlePinContextMenuCapture = useCallback(
    event => {
      openPinContextMenu(editor, shape.id, event);
    },
    [editor, shape.id]
  );

  const bringHoveredPinToFront = useCallback(() => {
    editor.run(() => {
      editor.bringToFront([shape.id]);
    }, { history: 'ignore' });
  }, [editor, shape.id]);

  const handleOpenFocus = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const id = String(shape.meta?.activityId || '').trim();
    if (!id) return;
    setFocusedPinId(id);
    setSelectedActivityId(id);
  }, [setFocusedPinId, setSelectedActivityId, shape.meta?.activityId]);

  const handleCardPointerDown = useCallback(
    (event, { onClick, allowDrag = true } = {}) => {
      if (!allowPinInteraction) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId: shape.id,
        shapeType: 'pin-card',
        historyLabel: 'pin',
        allowDrag,
        getDragIds: () => {
          const movingIds = editor
            .getSelectedShapeIds()
            .filter(id => {
              const selected = editor.getShape(id);
              return selected && selected.type === 'pin-card';
            });
          return movingIds.includes(shape.id) ? movingIds : [shape.id];
        },
        onClick,
        onDrop: () => {
          // Immediate + next-tick flush so membership meta and final coords both save.
          flushBoardPlacements();
          queueMicrotask(() => flushBoardPlacements());
        },
      });
    },
    [editor, shape.id, allowPinInteraction]
  );

  if (!activity?.content?.[0] || (activity?.pendingUrlLoad && !youtubeVideo)) {
    const domain = getPinDomain(activity) || 'Page';
    const host = activity?.content?.[0]?.urls?.[0]?.host || '';
    const faviconSrc = host ? `https://${host}/favicon.ico` : noFavicon;
    const isLoading = Boolean(activity?.pendingUrlLoad);
    return (
      <HTMLContainer
        id={shape.id}
        className="board-pin-card-container"
        style={{
          width: shape.props.w,
          height: shape.props.h,
          pointerEvents: 'none',
        }}
        onContextMenuCapture={handlePinContextMenuCapture}
      >
        <Box
          className={`board-pin-card ${isLoading ? 'board-pin-card--loading' : 'board-pin-card--placeholder'}`}
          onPointerDown={
            allowPinInteraction
              ? e => handleCardPointerDown(e, { allowDrag: true })
              : undefined
          }
          onDoubleClick={allowPinInteraction ? handleOpenFocus : undefined}
          style={{ pointerEvents: allowPinInteraction ? 'all' : 'none' }}
        >
          <Box className="board-pin-card-loading-header">
            <img
              src={faviconSrc}
              alt=""
              draggable={false}
              onError={e => {
                e.target.onerror = null;
                e.target.src = noFavicon;
              }}
            />
            <Typography
              variant="body2"
              className="board-pin-card-loading-title"
              style={ledgerHeaderText ? { color: LEDGER_ACCENT_ON_LIGHT, fontWeight: 700 } : undefined}
            >
              {ledgerHeaderText || activity?.content?.[0]?.title || domain}
            </Typography>
          </Box>
          <Box className="board-pin-card-loading-body">
            {isLoading ? (
              <>
                <CircularProgress size={22} thickness={4} />
                <Typography variant="caption" color="text.secondary">
                  Loading page…
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                {domain}
              </Typography>
            )}
          </Box>
        </Box>
      </HTMLContainer>
    );
  }

  return (
    <HTMLContainer
      id={shape.id}
      className="board-pin-card-container"
      style={{
        width: shape.props.w,
        height: shape.props.h,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
      onContextMenuCapture={handlePinContextMenuCapture}
    >
      <div
        ref={contentRef}
        className="board-pin-card-measure"
        onDoubleClick={allowPinInteraction && !youtubeVideo ? handleOpenFocus : undefined}
      >
        <SimpleContent
          type="BOARD"
          activity={activity}
          isBookmarkLayout
          loadedTime={loadedTime}
          inverseZoom={1}
          viewZoom={viewZoom}
          cardLayoutWidth={shape.props.w}
          allowPinInteraction={allowPinInteraction}
          boardPinPassThrough={!allowPinInteraction}
          loadImageWhen={loadImageWhen}
          onCardPointerDown={handleCardPointerDown}
          onOpenYoutube={youtubeVideo ? openYoutubePlayback : undefined}
          onBoardPinHover={bringHoveredPinToFront}
          ledgerHeaderText={ledgerHeaderText}
        />
      </div>
    </HTMLContainer>
  );
}

export class PinCardShapeUtil extends BaseBoxShapeUtil {
  static type = 'pin-card';

  static props = pinCardShapeProps;

  canResize() {
    return false;
  }

  hideResizeHandles() {
    return true;
  }

  hideRotateHandle() {
    return true;
  }

  isAspectRatioLocked() {
    return true;
  }

  hideSelectionBoundsFg() {
    return true;
  }

  hideSelectionBoundsBg() {
    return true;
  }

  getDefaultProps() {
    return {
      w: 300,
      h: 400,
    };
  }

  component(shape) {
    return <PinCardShapeComponent shape={shape} />;
  }

  indicator(shape) {
    return (
      <rect
        width={toDomPrecision(shape.props.w)}
        height={toDomPrecision(shape.props.h)}
        rx={14}
        ry={14}
      />
    );
  }
}
